package com.downdogapp.client.views

import com.downdogapp.*
import com.downdogapp.AlignItems.CENTER
import com.downdogapp.BoxSizing.BORDER_BOX
import com.downdogapp.Color.Companion.WHITE
import com.downdogapp.Display.FLEX
import com.downdogapp.Display.NONE
import com.downdogapp.FlexDirection.COLUMN
import com.downdogapp.FontWeight.MEDIUM
import com.downdogapp.JustifyContent.SPACE_BETWEEN
import com.downdogapp.Size.Companion.px
import com.downdogapp.client.MANIFEST
import com.downdogapp.client.Strings
import com.downdogapp.client.View
import com.downdogapp.client.api.AppType.*
import com.downdogapp.client.controllers.SignInViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.App
import com.downdogapp.client.singleton.Browser
import com.downdogapp.client.singleton.Logger
import com.downdogapp.client.widget.*
import com.downdogapp.client.widget.ScreenWidth.PHONE
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

actual class SignInView actual constructor(
  private val controller: SignInViewController
) : TopLevelView(), View {

  private var emailInput: HTMLInputElement
  private var passwordInput: HTMLInputElement
  private var thirdPartyContainer: HTMLDivElement

  private val google = window.asDynamic().google

  actual val email get() = emailInput.value.trim()
  actual val password get() = passwordInput.value

  private var googleLoginButton: HTMLDivElement? = null

  override val root =
    controllerView {
      bg = background(image = Images.LOGIN) {
        left = px(0)
        style.transform = "scale(1.25) translateY(-10%)"
      }

      fg = foreground {
        display = FLEX
        flexDirection = COLUMN

        screenWidthDependent { screenWidth ->
          console.log(screenWidth)
          justifyContent = if (screenWidth == PHONE) JustifyContent.FLEX_START else SPACE_BETWEEN
          paddingVertical = if (screenWidth == PHONE) px(0) else px(60)
        }
        alignItems = CENTER
        boxSizing = BORDER_BOX

        verticalFlexBox(horizontalAlign = CENTER) {
          marginTop = px(40)
          width = px(SignInViewConstants.contentWidth)

          imageView(Images.LOGIN_LOGO)
          label(controller.titleLabel, 20, MEDIUM, textAlign = TextAlign.CENTER) {
            width = px(300)
            marginTop = px(30)
          }
          controller.subtitleLabel?.let {
            label(it, 20, MEDIUM, textAlign = TextAlign.CENTER)
          }
        }

        verticalFlexBox(horizontalAlign = CENTER) {
          screenWidthDependent { screenWidth ->
            marginTop = px(if (screenWidth == PHONE) 120 else 0)
          }

          thirdPartyContainer = horizontalFlexBox(horizontalJustify = SPACE_BETWEEN) {
            width = px(160)

            if (MANIFEST.displayAppleSignInButton) {
              imageButton(Images.APPLE_LOGIN_SMALL_ICON, clickFn = ::onAppleClicked)
            }

            if (MANIFEST.displayGoogleSignInButton) {
              div {
                height = px(44)
                minHeight = px(44)

                googleLoginButton = div {
                  id = "google-login-btn"
                }
              }
            }

            if (MANIFEST.displayFacebookSignInButton) {
              imageButton(Images.FACEBOOK_LOGIN_SMALL_ICON, clickFn = ::onFacebookClicked)
            }
          }
            .also {
              it.opacity = 0.0
            }

          if (controller.numThirdPartyButtons > 0) {
            label(Strings.OR, 15) {
              marginVertical = px(15)
            }
          }
          form {
            display = FLEX
            flexDirection = COLUMN
            justifyContent = JustifyContent.CENTER

            action = ""
            onsubmit = {
              it.preventDefault()
              signUpLoginClicked()
            }
            emailInput = loginTextInput {
              type = "text"
              placeholder = Strings.EMAIL
            }

            passwordInput = loginTextInput {
              marginTop = px(15)
              type = "password"
              placeholder = Strings.PASSWORD
            }

            input {
              type = "submit"
              hidden = true
              display = NONE

              onsubmit = {
                it.preventDefault()
                it.stopPropagation()
                signUpLoginClicked()
              }
            }
          }
          roundedTextButton(
            if (controller.isSignUp) Strings.SIGN_UP else Strings.LOG_IN,
            height = px(SignInViewConstants.roundedButtonHeight),
            clickFn = ::signUpLoginClicked
          ) {
            maxWidth = px(SignInViewConstants.contentWidth)
          }
          if (!controller.isSignUp) {
            textButton(
              Strings.FORGOT_PASSWORD,
              12,
              MEDIUM,
              textAlign = TextAlign.CENTER,
              clickFn = controller::forgotPasswordClicked
            ) {
              marginTop = px(15)
            }
          }
        }
      }
    }

  init {
    refreshView()
    attachGoogleClickHandlers()
    attachAppleClickListeners()

    window.setTimeout({ fadeInThirdPartyLoginButtons() }, 250)
  }

  private fun fadeInThirdPartyLoginButtons() {
    var intervalId = -1

    @Suppress("ASSIGNED_VALUE_IS_NEVER_READ")
    intervalId = window.setInterval(
      {
        thirdPartyContainer.opacity?.let {
          if (it < 1.0) {
            thirdPartyContainer.opacity = it + 0.05
          } else {
            window.clearInterval(intervalId)
          }
        }
      },
      25
    )
  }

  // https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
  private fun decodeJWT(jwt: String): dynamic {
    val parts = jwt.split('.')
    if (parts.size != 3) {
      throw IllegalArgumentException("Invalid JWT token")
    }
    val payload = parts[1]
    val base64 = payload.replace('-', '+').replace('_', '/')
    val decodedPayload = window.atob(base64)

    val obj = kotlin.js.JSON.parse<Any>(decodedPayload)

    return obj
  }

  private fun attachGoogleClickHandlers() {
    google.accounts.id.initialize(
      jsObj(
        "client_id" to when (App.appType) {
          INTRO -> "912054047445-nh71uvkobsc9cgvesu2ks96tvii6ot5d.apps.googleusercontent.com"
          ORIGINAL -> "130396276383-uljreuca0pgkfg24f5q3lcidvbbj98bl.apps.googleusercontent.com"
          HIIT -> "930994540424-0jnppo93kc7u8mungq8dngqj7ccp17h5.apps.googleusercontent.com"
          BARRE -> "501492402189-0f8j3pea9rrrgkp4c9q0krunlebv3j9o.apps.googleusercontent.com"
          SEVEN -> "883348722937-purhq7rl8k926npu7410pa4pqm8vu4ht.apps.googleusercontent.com"
          PRENATAL -> "203141501385-k5pmt5ru00cl8j5gqkpvfu7i57381q09.apps.googleusercontent.com"
          MEDITATION -> "954547366638-ljl2t13sbkrsn9prp28a7iujdgkm1eae.apps.googleusercontent.com"
          RUNNING -> "97083762042-uquag8askfae8q51l6hg778b5olgjij7.apps.googleusercontent.com"
          BREATHING -> "204786321217-ai834650ubc6r84qr1pvrmj9hg5dqmrb.apps.googleusercontent.com"
          PILATES -> "854651578025-puu82ro7lqdprglpofja5403lro6hhr7.apps.googleusercontent.com"
        },
        "use_fedcm_for_prompt" to true,
        "cancel_on_tap_outside" to Browser.isPhone,
        "callback" to { credentialResponse: dynamic -> onGoogleAuthSuccess(credentialResponse) }
      )
    )

    googleLoginButton?.let {
      google.accounts.id.renderButton(
        it,
        jsObj(
          "type" to "icon",
          "size" to "large",
          "click_listener" to { spinnerShowing = true },
        )
      )
    }
  }

  private fun attachAppleClickListeners() {
    document.addEventListener("AppleIDSignInOnSuccess", {
      controller.appleLogin(it.asDynamic().detail.authorization.id_token)
    })
    document.addEventListener("AppleIDSignInOnFailure", {
      spinnerShowing = false
      App.alert(message = Strings.LOGIN_ERROR)
    })
  }

  private fun signUpLoginClicked() {
    Logger.logEvent("login_with_email_password", mapOf())
    controller.submitClicked()
  }

  private fun onAppleClicked() {
    spinnerShowing = true
    js("window.AppleID").auth.signIn()
  }

  private fun onFacebookClicked() {
    spinnerShowing = true
    val fb = window.asDynamic().FB
    val currentUserAuth = fb.getAuthResponse()

    if (currentUserAuth != null && currentUserAuth != undefined) {
      fetchFacebookUserInfo(currentUserAuth.accessToken)
    } else {
      fb.login(
        { loginResponse ->
          if (loginResponse.status == "connected") {
            fetchFacebookUserInfo(loginResponse.authResponse.accessToken)
          } else {
            spinnerShowing = false
            App.alert(
              title = Strings.FACEBOOK_AUTHENTICATION_FAILURE_MESSAGE,
              message = loginResponse.status as String
            )
          }
        },
        jsObj("scope" to "public_profile,email")
      )
    }
  }

  private fun fetchFacebookUserInfo(idToken: String) {
    val params = jsObj("fields" to "first_name, last_name, email")
    js("FB").api("/me", params) { apiResponse ->
      val email = apiResponse.email as String

      if (email.isBlank()) {
        spinnerShowing = false
        App.alert(message = Strings.NO_FACEBOOK_EMAIL_MESSAGE)
      } else {
        controller.facebookLogin(
          email = apiResponse.email as String,
          idToken = idToken,
          firstName = apiResponse.first_name as String,
          lastName = apiResponse.last_name as String
        )
      }
    }
  }

  private fun onGoogleAuthSuccess(credentialResponse: dynamic) {
    try {
      spinnerShowing = true

      val credential = credentialResponse.credential as String
      val credentialDecoded = decodeJWT(credential)

      val fullName = credentialDecoded.name as String
      val firstName = fullName.substringBeforeLast(' ')
      val lastName = fullName.substringAfterLast(' ', "")

      controller.googleLogin(
        email = credentialDecoded.email as String,
        idToken = credential,
        firstName = firstName,
        lastName = lastName
      )
    } catch (e: Exception) {
      App.alert(
        title = "Error authenticating Google",
        message = e.stackTraceToString()
      )
    }
  }

  private fun HTMLElement.loginTextInput(block: HTMLInputElement.() -> Unit = {}) =
    input {
      classList.add("no-outline-focus") // Implemented in web.css
      (this as HTMLElement).width = px(SignInViewConstants.contentWidth)
      (this as HTMLElement).height = px(SignInViewConstants.roundedButtonHeight)

      bgColor = Color.TEXT_INPUT_BACKGROUND
      border = Border.NONE
      borderRadius = px(25)
      txtColor = WHITE
      paddingLeft = px(25)

      fontFamily = FONT_FAMILY
      fontWeight = MEDIUM
      fontSize = px(15)

      block()
    }

  actual fun focusEmail() {}
}
